'use client';
import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { supabase } from '@/lib/supabase';
import CustomerDashboardLayout from '../components/CustomerDashboardLayout';

interface WishlistItem {
  id: string;
  product_name: string;
  product_image: string | null;
  price: number;
  store_name: string;
  created_at: string;
}

export default function CustomerWishlistPage() {
  const router = useRouter();
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadWishlist = async (uid: string) => {
    const { data } = await supabase.from('customer_wishlist').select('*').eq('customer_id', uid).order('created_at', { ascending: false });
    setItems(data || []);
    setLoading(false);
  };

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      if (!user) { router.push('/customer-login'); return; }
      setUserId(user.id);
      loadWishlist(user.id);
    });
  }, [router]);

  const handleRemove = async (id: string) => {
    const { error } = await supabase.from('customer_wishlist').delete().eq('id', id);
    if (!error) { showToast('Removed from wishlist.', 'success'); loadWishlist(userId); }
    else showToast('Failed to remove item.', 'error');
  };

  return (
    <CustomerDashboardLayout>
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-xl text-sm font-500 shadow-lg flex items-center gap-2 ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'ExclamationTriangleIcon'} size={16} />
          {toast.msg}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-700 text-slate-900">My Wishlist</h1>
          <p className="text-sm text-slate-500 mt-0.5">Products you&apos;ve saved for later</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Icon name="ArrowPathIcon" size={28} className="animate-spin text-primary" />
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-xl py-16 text-center">
            <Icon name="HeartIcon" size={32} className="text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-400">Your wishlist is empty</p>
            <p className="text-xs text-slate-400 mt-1">Save products you love to find them easily later</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(item => (
              <div key={item.id} className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
                <div className="h-40 bg-slate-100 flex items-center justify-center">
                  {item.product_image ? (
                    <img src={item.product_image} alt={item.product_name} className="w-full h-full object-cover" />
                  ) : (
                    <Icon name="PhotoIcon" size={32} className="text-slate-300" />
                  )}
                </div>
                <div className="p-4">
                  <p className="text-sm font-700 text-slate-900 truncate">{item.product_name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{item.store_name}</p>
                  <div className="flex items-center justify-between mt-3">
                    <span className="text-base font-700 text-primary">₹{item.price?.toLocaleString('en-IN')}</span>
                    <button
                      onClick={() => handleRemove(item.id)}
                      className="p-1.5 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition-colors"
                      aria-label="Remove from wishlist"
                    >
                      <Icon name="TrashIcon" size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </CustomerDashboardLayout>
  );
}
